import React from 'react';
import { AlertCircle, Check } from 'lucide-react';
import type { AnalisisImagen } from './tipos';
import { colorDe, segmentosDe } from './estado';

/** Cuatro segmentos; se pintan tantos como el puntaje. Sin número, a propósito. */
export const Segmentos: React.FC<{ pintados: number; color: string; ancho: number; alto: number }> = ({
  pintados,
  color,
  ancho,
  alto,
}) => (
  <span className="inline-flex items-center gap-[2px]" aria-hidden>
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="rounded-full"
        style={{ width: ancho, height: alto, backgroundColor: i < pintados ? color : 'rgba(255,255,255,0.35)' }}
      />
    ))}
  </span>
);

/**
 * El resultado: UNA pastilla con fondo propio, en la esquina SUPERIOR derecha de la
 * miniatura. Adentro, "Analizada" y los segmentos JUNTOS — pedido de Mati (2026-09-07):
 * antes el texto quedaba pegado al borde izquierdo y el puntaje al derecho, a media card
 * de distancia, y los segmentos flotaban sobre la foto sin nada atrás, así que sobre una
 * imagen clara se perdían.
 *
 * Arriba y no abajo (segundo pedido del mismo día): el borde inferior ya lo ocupan el
 * rótulo de producto/hora y el botón de analizar, y el resultado es lo que se recorre de
 * un vistazo cuando la grilla tiene varias analizadas. Comparte fila con el chip del modo
 * (arriba a la izquierda), que trunca antes de llegar.
 *
 * Toda la pastilla es el botón que abre el popover.
 */
export const BarraResultado: React.FC<{
  analisis: AnalisisImagen | null;
  error?: string | null;
  onAbrir: (ancla: DOMRect) => void;
}> = ({ analisis, error, onAbrir }) => {
  const conError = !!error || !analisis || analisis.estado !== 'lista';
  return (
    <button
      type="button"
      aria-label={conError ? 'No se pudo analizar. Ver detalle' : 'Analizada. Ver resultado'}
      onClick={(e) => {
        e.stopPropagation();
        onAbrir(e.currentTarget.getBoundingClientRect());
      }}
      className="absolute right-2 top-2 z-30 inline-flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-full bg-black/55 py-1 px-2 text-left backdrop-blur-sm transition-colors hover:bg-black/70"
    >
      <span className="inline-flex min-w-0 items-center gap-1 text-[11.5px] font-semibold leading-tight text-white">
        {conError ? <AlertCircle className="h-3 w-3 shrink-0" /> : <Check className="h-3 w-3 shrink-0" />}
        <span className="truncate">{conError ? 'No se pudo analizar' : 'Analizada'}</span>
      </span>
      {/* Con error no hay puntaje que mostrar: cuatro segmentos apagados sugerirían un 0
          que el modelo nunca dio. */}
      {!conError && analisis && (
        <Segmentos pintados={segmentosDe(analisis)} color={colorDe(analisis)} ancho={12} alto={4} />
      )}
    </button>
  );
};
